import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Edit, Printer, ArrowUpDown, ArrowDownNarrowWide, ArrowDownWideNarrow, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { projectsAPI, showConfirmModal } from '../services/api';
import { toast } from 'sonner';
import { readResponseJSON } from '../utils/fetchSafe';
import { useCommissionsQuery } from '../hooks/useCommissionsQuery';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCurrency = (val) => {
  const n = parseFloat(val) || 0;
  const prefix = n < 0 ? '-' : '';
  return `${prefix}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const FilterDropdown = ({ options, value, onChange, testId }) => (
  <div className="relative inline-block">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-gray-300 rounded px-2 py-1 text-xs w-full bg-white cursor-pointer"
      data-testid={testId}
    >
      <option value=""></option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const SortHeader = ({ label, column, sort, onSort, testId, red }) => {
  const active = sort.column === column;
  return (
    <th className="text-left p-2 font-normal">
      <button onClick={() => onSort(column)} className="flex items-center gap-1 hover:text-gray-800" data-testid={testId}>
        {active
          ? (sort.direction === 'asc' ? <ArrowDownNarrowWide className="w-3 h-3 text-red-500" /> : <ArrowDownWideNarrow className="w-3 h-3 text-red-500" />)
          : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
        <span className={red ? 'text-red-500' : (active ? 'text-red-500' : 'text-gray-600')} dangerouslySetInnerHTML={{ __html: label }} />
      </button>
    </th>
  );
};

const PaidCommissions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterManager, setFilterManager] = useState('');
  const [filterType, setFilterType] = useState('');
  const [managers, setManagers] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [sort, setSort] = useState({ column: '', direction: 'asc' });
  const [reloadKey, setReloadKey] = useState(0);

  const { rows, totalCount, loading } = useCommissionsQuery({
    view: 'paid',
    page: currentPage,
    pageSize: entriesPerPage,
    search: searchTerm,
    manager: filterManager,
    jobType: filterType,
    sortBy: sort.column,
    sortDir: sort.direction,
    reloadKey,
  });

  useEffect(() => {
    fetch(`${API_URL}/project-types`)
      .then(readResponseJSON)
      .then(d => setProjectTypes((d.project_types || []).map(t => t.name).sort()))
      .catch(() => {});

    fetch(`${API_URL}/employees?active=true&role=Production`)
      .then(readResponseJSON)
      .then(d => {
        const prodEmps = (d.employees || []).map(e => `${e.last_name}, ${e.first_name}`).sort();
        setManagers(prodEmps);
      })
      .catch(() => {});
  }, []);

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    e.preventDefault();
    if (!(await showConfirmModal('Are you sure you want to delete this project?'))) return;
    try {
      await projectsAPI.delete(projectId);
      toast.success('Project deleted');
      setReloadKey(k => k + 1);
    } catch (error) {
      if (error.response?.status === 401) return;
      const detail = error.response?.data?.detail || 'Failed to delete project';
      toast.error(detail);
    }
  };

  const handleSort = (column) => {
    setSort(prev => prev.column === column
      ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: 'asc' });
    setCurrentPage(1);
  };

  const pageData = rows;
  const totalPages = Math.max(1, Math.ceil(totalCount / entriesPerPage));
  const startIdx = (currentPage - 1) * entriesPerPage;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light mb-1" data-testid="paid-subtitle">Commissions</h2>
          <h1 className="text-3xl lg:text-4xl text-[#5B8DB8] font-light" data-testid="paid-title">Paid Commissions</h1>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show</span>
              <select value={entriesPerPage} onChange={e => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded px-3 py-1 text-sm" data-testid="entries-select">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">entries</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Search:</span>
              <input type="text" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="border border-gray-300 rounded px-3 py-1 text-sm w-48" data-testid="paid-search" />
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading paid commissions...</div>
            ) : (
              <>
                <table className="w-full text-xs" data-testid="paid-table">
                  <thead>
                    <tr className="border-b-2">
                      <th className="text-left p-2 text-gray-600 font-normal">Project #</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Project</th>
                      <SortHeader label="Finished Date" column="finished_date" sort={sort} onSort={handleSort} testId="sort-finished-date" />
                      <SortHeader label="Production<br/>Manager" column="production_manager" sort={sort} onSort={handleSort} testId="sort-pm" red />
                      <th className="text-left p-2 text-gray-600 font-normal">Percentage</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Type</th>
                      <SortHeader label="Gross Value" column="subtotal" sort={sort} onSort={handleSort} testId="sort-gross-value" />
                      <th className="text-left p-2 text-gray-600 font-normal">Paid in Full</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Expenses</th>
                      <SortHeader label="Net Value" column="commissionable_total" sort={sort} onSort={handleSort} testId="sort-net-value" />
                      <th className="text-left p-2 text-gray-600 font-normal">Commission %</th>
                      <SortHeader label="Net Commission" column="gross_commission" sort={sort} onSort={handleSort} testId="sort-net-commission" />
                      <th className="text-left p-2 text-gray-600 font-normal">Mgmt Expenses</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Labor</th>
                      <SortHeader label="Advances" column="advances" sort={sort} onSort={handleSort} testId="sort-advances" />
                      <SortHeader label="Commission<br/>Payable" column="_commission_payable" sort={sort} onSort={handleSort} testId="sort-commission-payable" />
                      <th className="p-2"></th>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2">
                        <FilterDropdown options={managers} value={filterManager} onChange={v => { setFilterManager(v); setCurrentPage(1); }} testId="filter-manager" />
                      </td>
                      <td className="p-2"></td>
                      <td className="p-2">
                        <FilterDropdown options={projectTypes} value={filterType} onChange={v => { setFilterType(v); setCurrentPage(1); }} testId="filter-type" />
                      </td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((item, index) => (
                      <tr key={index} className={`border-b hover:bg-gray-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} onClick={() => navigate(`/projects/${item.project_id}`)} data-testid={`paid-row-${index}`}>
                        <td className="p-2 text-gray-700">{item.project_id}</td>
                        <td className="p-2">
                          <div className="text-gray-700">{item.customer}</div>
                          <div className="text-xs text-gray-400">{item.location}</div>
                        </td>
                        <td className="p-2 text-gray-700">{item.finished_date || 'n/a'}</td>
                        <td className="p-2 text-gray-700">{item.production_manager || 'n/a'}</td>
                        <td className="p-2 text-gray-700">{item.commission_percent}%</td>
                        <td className="p-2 text-gray-700">{item.type}</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.subtotal)}</td>
                        <td className="p-2 text-gray-700">Yes</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.non_mgmt_expenses)}</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.commissionable_total)}</td>
                        <td className="p-2 text-gray-700">{item.commission_percent}%</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.gross_commission)}</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.mgmt_expenses)}</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.labor_fuel_vehicle)}</td>
                        <td className="p-2 text-gray-700">{fmtCurrency(item.advances)}</td>
                        <td className="p-2 text-gray-700">$0.00</td>
                        <td className="p-2" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <a href={`/projects/${item.project_id}`} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1 inline-flex" data-testid={`edit-btn-${index}`}>
                              <Edit className="w-3 h-3" />
                            </a>
                            <a href={`/commission-report/${item.project_id}`} className="bg-[#8DB600] hover:bg-[#7BA300] text-white rounded p-1 inline-flex" data-testid={`print-btn-${index}`}>
                              <Printer className="w-3 h-3" />
                            </a>
                            {user?.is_admin && (
                            <button onClick={(e) => handleDeleteProject(e, item.project_id)} className="text-[#DC3545] hover:text-[#C82333]" data-testid={`delete-btn-${index}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pageData.length === 0 && (
                      <tr><td colSpan="17" className="text-center py-8 text-gray-500">No paid commissions found</td></tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-600" data-testid="showing-text">
                    Showing {totalCount === 0 ? 0 : startIdx + 1} to {startIdx + pageData.length} of {totalCount} entries
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1" data-testid="pagination">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="page-prev"
                      >
                        Previous
                      </button>
                      {getPageNumbers().map((page, i) =>
                        page === '...' ? (
                          <span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 text-sm border rounded ${currentPage === page ? 'bg-red-500 text-white border-red-500' : 'hover:bg-gray-100'}`}
                            data-testid={`page-${page}`}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="page-next"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaidCommissions;
